import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  HStack,
  Input,
  Modal,
  Stack,
  Switch,
  Text,
  Textarea,
} from "../../../../components/ui";
import {
  AcceptSuggestionRequest,
  Suggestion,
} from "../../../../services/suggestionService";
import SkillDiff from "./SkillDiff";

interface SkillDraftDialogProps {
  suggestion: Suggestion | null;
  onClose: () => void;
  onConfirm: (body: AcceptSuggestionRequest) => Promise<void>;
}

/**
 * Review-and-edit step before a Skill or SkillAmendment suggestion becomes a real skill. The admin
 * sees exactly what the agent will be told and can rewrite any of it; for an amendment the existing
 * skill is named and only the appended section is editable.
 */
const SkillDraftDialog: React.FC<SkillDraftDialogProps> = ({
  suggestion,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const isAmendment = suggestion?.kind === "SkillAmendment";
  const draft = suggestion?.draft ?? {};

  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [trigger, setTrigger] = useState("");
  const [prompt, setPrompt] = useState("");
  const [assignToWidget, setAssignToWidget] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!suggestion) return;
    const d = suggestion.draft ?? {};
    setName(d.name ?? suggestion.targetSkillName ?? suggestion.title);
    setCategory(d.category ?? "");
    setDescription(d.humanReadableDescription ?? "");
    setTrigger(d.triggerContext ?? "");
    setPrompt(d.skillPrompt ?? "");
    setAssignToWidget(true);
  }, [suggestion]);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm({
        skillName: isAmendment ? undefined : name,
        skillCategory: isAmendment ? undefined : category,
        skillDescription: isAmendment ? undefined : description,
        triggerContext: isAmendment ? undefined : trigger,
        skillPrompt: prompt,
        assignToWidget,
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDisabled =
    saving ||
    !prompt.trim() ||
    (!isAmendment && (!name.trim() || !trigger.trim()));

  return (
    <Modal isOpen={!!suggestion} onClose={onClose} size="xl">
      <Modal.Header>
        <Text fontWeight={600} fontSize="md" color="var(--text-primary)">
          {isAmendment
            ? t("app.suggestions.skillDialog.amendTitle", {
                skill:
                  suggestion?.targetSkillName ?? draft.targetSkillName ?? "",
              })
            : t("app.suggestions.skillDialog.title")}
        </Text>
      </Modal.Header>

      <Modal.Body>
        <Stack gap={4}>
          {suggestion?.rationale && (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {suggestion.rationale}
            </Text>
          )}

          {!isAmendment && (
            <>
              <Stack gap={1}>
                <Text
                  fontSize="sm"
                  fontWeight={500}
                  color="var(--text-primary)"
                >
                  {t("app.suggestions.skillDialog.name")}
                </Text>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  size="sm"
                />
              </Stack>
              <Stack gap={1}>
                <Text
                  fontSize="sm"
                  fontWeight={500}
                  color="var(--text-primary)"
                >
                  {t("app.suggestions.skillDialog.category")}
                </Text>
                <Input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  size="sm"
                />
              </Stack>
              <Stack gap={1}>
                <Text
                  fontSize="sm"
                  fontWeight={500}
                  color="var(--text-primary)"
                >
                  {t("app.suggestions.skillDialog.description")}
                </Text>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </Stack>
              <Stack gap={1}>
                <Text
                  fontSize="sm"
                  fontWeight={500}
                  color="var(--text-primary)"
                >
                  {t("app.suggestions.skillDialog.trigger")}
                </Text>
                <Input
                  value={trigger}
                  onChange={(e) => setTrigger(e.target.value)}
                  size="sm"
                />
              </Stack>
            </>
          )}

          <Stack gap={1}>
            <Text fontSize="sm" fontWeight={500} color="var(--text-primary)">
              {isAmendment
                ? t("app.suggestions.skillDialog.addition")
                : t("app.suggestions.skillDialog.prompt")}
            </Text>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={14}
              fontFamily="var(--font-mono)"
              fontSize="xs"
            />
            {isAmendment && (
              <Text fontSize="xs" color="var(--text-secondary-color)">
                {t("app.suggestions.skillDialog.amendHint")}
              </Text>
            )}
            {isAmendment && (
              // Live: edits to the section above show up on the right as they are typed.
              <SkillDiff
                current={suggestion?.targetSkillPrompt ?? ""}
                proposed={
                  (suggestion?.targetSkillPrompt ?? "").length > 0
                    ? `${(suggestion?.targetSkillPrompt ?? "").replace(/\s+$/, "")}\n\n${prompt}`
                    : prompt
                }
              />
            )}
          </Stack>

          {!isAmendment && (
            <HStack justify="space-between">
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight={500}
                  color="var(--text-primary)"
                >
                  {t("app.suggestions.skillDialog.assignToWidget")}
                </Text>
                <Text fontSize="xs" color="var(--text-secondary-color)">
                  {t("app.suggestions.skillDialog.assignToWidgetHint")}
                </Text>
              </Box>
              <Switch
                checked={assignToWidget}
                onCheckedChange={(e: { checked: boolean }) =>
                  setAssignToWidget(e.checked)
                }
                colorPalette="blue"
              />
            </HStack>
          )}
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <HStack justify="flex-end" gap={3}>
          <Button intent="secondary" onClick={onClose} disabled={saving}>
            {t("app.common.cancel")}
          </Button>
          <Button
            intent="primary"
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            {isAmendment
              ? t("app.suggestions.skillDialog.confirmAmend")
              : t("app.suggestions.skillDialog.confirm")}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

export default SkillDraftDialog;
